<template>
  <div class="w-full h-full flex">
    <CList
      ref="listRef"
      class="folders-files-table rounded-2 flex bg-base"
      :data="displayedFilesFolders"
      :maxRowHeight="150"
      :movable="appName === 'admin'"
      :selectedIds="selectedItemIds"
      @item:click="onItemClick"
      @move="onMove"
    >
      <template #item="{ item, isSelected, isDraggedOver, isContextMenuOpen }">
        <BrowserListItem
          :do-not-show-folder-details="!!apiState.searchQuery"
          :filters="computed(() => ({}) as Record<string, any>)"
          :highlight-text="apiState.searchQuery"
          :is-context-menu-open="isContextMenuOpen"
          :is-dragged-over="isDraggedOver"
          :is-selected="isSelected"
          :item="item"
          :metadata="processedMetadata[item.id] || {}"
          :style="{ opacity: item.opacity || 1 }"
        >
          <template #actions>
            <FileDetailsPanelButton :item="getTypedItem(item)" />
            <FavoriteButton v-if="enabledFavorites" :item="getTypedItem(item)" />
            <CButton
              v-tooltip="t('canvasUI.browserApp.contextMenu.tooltip')"
              circle
              class="h-9 w-9"
              secondary
              type="default"
              @click="toggleCtxMenu(getTypedItem(item), isContextMenuOpen, $event)"
            >
              <template #icon>
                <CIcon class="ml-[2px] color-primary" icon="ellipsis" size="12" />
              </template>
            </CButton>
          </template>
        </BrowserListItem>
      </template>
      <template #item:context-menu="{ item }">
        <ContextMenuDropdown
          :item="getTypedItem(item)"
          @click="() => toggleCtxMenu(getTypedItem(item), true)"
          @open-details-panel="selectAndOpenContentPanel"
          @toggle-favorite="toggleItemFavorite"
        />
      </template>
    </CList>
  </div>
</template>

<script lang="ts" setup>
import { ref, computed, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'

import BrowserListItem from '@browser/components/Content/Common/BrowserListItem.vue'
import CButton from '@lib/components/CButton/CButton.vue'
import CIcon from '@lib/components/CIcon/CIcon.vue'
import CList from '@lib/components/CList/CList.vue'
import ContextMenuDropdown from '@lib/apps/browser/components/ContextMenu/ContextMenuDropdown.vue'
import FavoriteButton from '@browser/components/ContextMenu/FavoriteButton.vue'
import FileDetailsPanelButton from '@browser/components/ContextMenu/FileDetailsPanelButton.vue'

import useApiStore from '@browser/stores/api'
import useAppStore from '@browser/stores/app'
import useMetadataTemplates from '@lib/composables/useMetadataTemplates'

import { openItem } from '@browser/util/file'

import type { RenderableFile, ViewCompactFileFolder, ViewCompactFolder } from '@lib/types/files'
import { type MiniFolder, type MiniFile } from '@openapi'

const listRef = ref<HTMLElement | null>(null)
const { t } = useI18n()
const { state: apiState } = useApiStore()
const {
  selectedItemIds,
  displayedFolders,
  displayedFilesFolders,
  inlineEditingItem,
  appName,
  selectAndOpenContentPanel,
  toggleItemFavorite,
  enabledFavorites,
} = useAppStore()

// Initialize metadata templates and create processed metadata
const { processedMetadata, fetchTemplate } = useMetadataTemplates({
  itemsRef: displayedFilesFolders,
})

const getTypedItem = (item: any) => item as ViewCompactFolder

const onMove = (event: { targetItemId: MiniFolder['id']; itemIds: string[] }) => {
  const targetFolderId = displayedFolders.value.find((f) => f.id === event.targetItemId)?.id
  if (!targetFolderId) return

  apiState.itemsMover?.(
    targetFolderId,
    event.itemIds.map((id) => ({ id, type: displayedFilesFolders.value.find((i) => i.id === id)?.type })) as {
      id: MiniFolder['id'] | MiniFile['id']
      type: 'folder' | 'file'
    }[],
  )
}

function onItemClick(item: RenderableFile, e: MouseEvent) {
  if (inlineEditingItem.value?.id) return

  openItem(item, e)
}

function toggleCtxMenu(item: ViewCompactFileFolder, isContextMenuOpen: boolean, e?: MouseEvent) {
  const listInstance = listRef.value as InstanceType<typeof CList> | null
  listInstance?.updateContextMenuItem(item, !isContextMenuOpen, e)
}

// Fetch metadata templates on mount
onMounted(async () => {
  await fetchTemplate()
})
</script>
